import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  setWorkspaceId,
  getAuthToken,
  setAuthToken,
  setUserId,
  clearAuth,
} from "@/lib/api";
import { queryKeys } from "@/lib/query-keys";

interface User {
  id: string;
  email: string;
  name: string | null;
  avatar_url: string | null;
}

interface Workspace {
  id: string;
  name: string;
  slug: string;
  plan: string;
  role: string;
}

interface OnboardingInfo {
  completed_at: number | null;
  website_url: string | null;
  asset_count: number;
  target_count: number;
}

interface StartupProject {
  id: string;
  name: string;
}

interface StartupMetadata {
  has_projects: boolean;
  default_project_id: string | null;
  default_project: StartupProject | null;
  project_count: number;
  project_roles: Record<string, string>;
  workspace_count: number;
}

interface MeResponse {
  user: User;
  current_workspace: Workspace;
  workspaces: Workspace[];
  allowed_project_ids: string[];
  onboarding?: OnboardingInfo;
  startup?: StartupMetadata;
}

interface AuthContextValue {
  user: User | null;
  workspace: Workspace | null;
  workspaces: Workspace[];
  allowedProjectIds: string[];
  onboarding: OnboardingInfo | null;
  startup: StartupMetadata | null;
  startupProject: StartupProject | null;
  isLoading: boolean;
  isLoggedIn: boolean;
  error: Error | null;
  login: (token: string, userId: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  workspace: null,
  workspaces: [],
  allowedProjectIds: [],
  onboarding: null,
  startup: null,
  startupProject: null,
  isLoading: true,
  isLoggedIn: false,
  error: null,
  login: () => {},
  logout: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [hasToken, setHasToken] = useState(() => !!getAuthToken());

  // Handle OAuth/magic-link callback tokens in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("auth_token");
    const urlUserId = params.get("user_id");
    if (urlToken) {
      setAuthToken(urlToken);
      if (urlUserId) setUserId(urlUserId);
      setHasToken(true);
      // Clean URL
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
      // Refetch /me
      queryClient.invalidateQueries({ queryKey: queryKeys.me() });
    }
  }, [queryClient]);

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.me(),
    queryFn: () => api<MeResponse>("/me"),
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled: hasToken,
  });

  // Set workspace ID for API client when workspace changes
  useEffect(() => {
    if (data?.current_workspace) {
      setWorkspaceId(data.current_workspace.id);
    }
  }, [data?.current_workspace]);

  const login = useCallback(
    (token: string, userId: string) => {
      setAuthToken(token);
      setUserId(userId);
      setHasToken(true);
      queryClient.invalidateQueries({ queryKey: queryKeys.me() });
    },
    [queryClient],
  );

  const logout = useCallback(() => {
    clearAuth();
    setHasToken(false);
    queryClient.clear();
    window.location.href = "/login";
  }, [queryClient]);

  const isLoggedIn = hasToken && !!data?.user;

  return (
    <AuthContext.Provider
      value={{
        user: data?.user ?? null,
        workspace: data?.current_workspace ?? null,
        workspaces: data?.workspaces ?? [],
        allowedProjectIds: data?.allowed_project_ids ?? [],
        onboarding: data?.onboarding ?? null,
        startup: data?.startup ?? null,
        startupProject: data?.startup?.default_project ?? null,
        isLoading: hasToken ? isLoading : false,
        isLoggedIn,
        error: error as Error | null,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
